import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

/**
 * Type definitions for the context
 */
interface ImageData {
  base64: string;
  file: File;
}

interface ImageUpload {
  userImage: ImageData | null;
  editedImage: ImageData | null;
}

interface TextInputs {
  [key: string]: string;
}

interface ImageInputs {
  [key: string]: ImageUpload;
}

interface VisualiserConfig {
  mapping: {
    text?: { [key: string]: string };
    images?: { [key: string]: string };
  };
}

interface ImageTransformation {
  scale: number;
  x: number;
  y: number;
}

interface VisualiserSelection {
  productKey: string | null;
  variant: unknown | null;
  config: VisualiserConfig | null;
  transformations: Record<string, ImageTransformation>;
}

interface UserInputContextType {
  // Text input methods
  getTextInput: (key: string) => string;
  setTextInput: (key: string, value: string) => void;
  clearTextInput: (key: string) => void;
  getAllTextInputs: () => TextInputs;
  
  // Image input methods
  getImageInput: (key: string) => ImageUpload | null;
  setImageInput: (key: string, value: ImageUpload) => void;
  clearImageInput: (key: string) => void;
  getAllImageInputs: () => ImageInputs;
  
  // Transformation methods
  getTransformation: (key: string) => ImageTransformation;
  setTransformation: (key: string, transformation: Partial<ImageTransformation>) => void;
  
  // Clear all
  clearAll: () => void;
  
  // Backwards-compatible alias used by legacy JS modules.
  clear: () => void;

  // Visualiser selection for inserts/cards summary.
  visualiserSelection: VisualiserSelection;
  setVisualiserSelection: (options: { productKey: string | null; variant: unknown | null; config?: VisualiserConfig | null }) => void;
  clearVisualiserSelection: () => void;
}

/**
 * Create the context with undefined default value
 */
const UserInputContext = createContext<UserInputContextType | undefined>(undefined);

/**
 * Props for the UserInputProvider
 */
interface UserInputProviderProps {
  children: ReactNode;
}

/**
 * UserInputProvider component that manages text and image inputs
 * 
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components
 * @returns {React.ReactElement}
 */
export function UserInputProvider({ children }: UserInputProviderProps): React.ReactElement {
  const [textInputs, setTextInputs] = useState<TextInputs>({});
  const [imageInputs, setImageInputs] = useState<ImageInputs>({});
  const [visualiserSelection, setVisualiserSelectionState] = useState<VisualiserSelection>({
    productKey: null,
    variant: null,
    config: null,
    transformations: {}
  });

  /**
   * Get text input value by key
   */
  const getTextInput = useCallback((key: string): string => {
    return textInputs?.[key] || '';
  }, [textInputs]);

  /**
   * Set text input value by key
   */
  const setTextInput = useCallback((key: string, value: string): void => {
    setTextInputs((prev) => ({
      ...prev,
      [key]: value
    }));
  }, []);

  /**
   * Clear text input by key
   */
  const clearTextInput = useCallback((key: string): void => {
    setTextInputs((prev) => {
      const newInputs = { ...prev };
      delete newInputs[key];
      return newInputs;
    });
  }, []);

  /**
   * Get all text inputs
   */
  const getAllTextInputs = useCallback((): TextInputs => {
    return { ...textInputs };
  }, [textInputs]);

  /**
   * Get image input by key
   */
  const getImageInput = useCallback((key: string): ImageUpload | null => {
    return imageInputs?.[key] || null;
  }, [imageInputs]);

  /**
   * Set image input by key
   * Compatible with FileInput component's onFileSelect callback
   */
  const setImageInput = useCallback((key: string, value: ImageUpload): void => {
    setImageInputs((prev) => ({
      ...prev,
      [key]: value
    }));
  }, []);

  /**
   * Clear image input by key
   */
  const clearImageInput = useCallback((key: string): void => {
    setImageInputs((prev) => {
      const newInputs = { ...prev };
      delete newInputs[key];
      return newInputs;
    });
  }, []);

  /**
   * Get all image inputs
   */
  const getAllImageInputs = useCallback((): ImageInputs => {
    return { ...imageInputs };
  }, [imageInputs]);

  /**
   * Get image transformation by key
   */
  const getTransformation = useCallback(
    (key: string): ImageTransformation => {
      return visualiserSelection.transformations[key] || { scale: 1, x: 0, y: 0 };
    },
    [visualiserSelection.transformations]
  );

  /**
   * Set image transformation by key
   */
  const setTransformation = useCallback(
    (key: string, transformation: Partial<ImageTransformation>): void => {
      console.log(key, transformation)
      setVisualiserSelectionState((prev) => ({
        ...prev,
        transformations: {
          ...prev.transformations,
          [key]: {
            ...(prev.transformations[key] || { scale: 1, x: 0, y: 0 }),
            ...transformation
          }
        }
      }));
    },
    []
  );

  /**
   * Clear all inputs (text and images)
   */
  const clearAll = useCallback((): void => {
    setTextInputs({});
    setImageInputs({});
    setVisualiserSelectionState({ productKey: null, variant: null, config: null, transformations: {} });
  }, []);

  /**
   * Backwards-compatible alias for clearAll.
   * This exists because some legacy JS modules call `userInput.clear()`.
   */
  const clear = useCallback((): void => {
    clearAll();
  }, [clearAll]);

  /**
   * Stores the current inserts/cards visualiser variant selection.
   * This is used to show the correct insert/card in the footer summary while on visualiser pages.
   */
  const setVisualiserSelection = useCallback(
    (options: { productKey: string | null; variant: unknown | null; config?: VisualiserConfig | null }): void => {
      const { productKey, variant, config } = options;
      setVisualiserSelectionState((prev) => ({
        ...prev,
        productKey: productKey ?? null,
        variant: variant ?? null,
        config: config ?? null
      }));
    },
    []
  );

  /**
   * Clears the inserts/cards visualiser selection.
   */
  const clearVisualiserSelection = useCallback((): void => {
    setVisualiserSelectionState({ productKey: null, variant: null, config: null, transformations: {} });
  }, []);

  const value: UserInputContextType = {
    getTextInput,
    setTextInput,
    clearTextInput,
    getAllTextInputs,
    getImageInput,
    setImageInput,
    clearImageInput,
    getAllImageInputs,
    getTransformation,
    setTransformation,
    clearAll,
    clear,
    visualiserSelection,
    setVisualiserSelection,
    clearVisualiserSelection
  };

  return (
    <UserInputContext.Provider value={value}>
      {children}
    </UserInputContext.Provider>
  );
}

/**
 * Custom hook to use the UserInput context
 * 
 * @throws {Error} If used outside of UserInputProvider
 * @returns {UserInputContextType} The context value
 */
export function useUserInput(): UserInputContextType {
  const context = useContext(UserInputContext);
  
  if (context === undefined) {
    throw new Error('useUserInput must be used within a UserInputProvider');
  }
  
  return context;
}